/** @jsx jsx */
import { defineMessages, useIntl } from 'react-intl-next';
import { keyframes } from '@compiled/react';

import { IconButton } from '@atlaskit/button/new';
import AddIcon from '@atlaskit/icon/core/add';
import TagIcon from '@atlaskit/icon/core/tag';
import { cssMap, jsx } from '@atlaskit/css';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';

import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import { EditLabelDialogLoader } from '@confluence/labels/entry-points/EditLabelDialogLoader';

import { useContentLabels } from '../hooks/useContentLabels';

import { InlineRetryError } from './InlineRetryError';
import { DetailRow } from './DetailRow';
import { Labels } from './Labels';

const i18n = defineMessages({
	labelsSectionTitle: {
		id: 'details-panel.labels-section.title',
		defaultMessage: 'Labels',
		description: 'Title for section containing labels related to content',
	},
	addButtonLabel: {
		id: 'details-panel.labels-section.add-button-label',
		defaultMessage: 'Add label',
		description: 'aria-label for button to add label for content',
	},
});

type LabelsSectionProps = {
	highlight?: boolean;
};

export const LabelsSection = ({ highlight }: LabelsSectionProps) => {
	const { formatMessage } = useIntl();
	const [contentId = ''] = usePageContentId();
	const [spaceKey = ''] = usePageSpaceKey();
	const { showModal } = useDialogs();
	const { data, error, refetch, handleDeleteLabelClick, loading } = useContentLabels();
	const { labels, hasEditPermissions } = data;

	const scrollToRef = (el: HTMLDivElement | null) => {
		if (el && highlight) {
			el.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	};

	const renderLabelsSectionContent = () => {
		if (error) {
			return <InlineRetryError error={error} refetch={refetch} />;
		}

		return hasEditPermissions ? (
			<UFOLoadHold name="labels-section-details-panel-hold" hold={loading}>
				<IconButton
					appearance="subtle"
					icon={AddIcon}
					label={formatMessage(i18n.addButtonLabel)}
					onClick={() =>
						showModal(EditLabelDialogLoader, {
							contentId,
							spaceKey,
							source: 'detailsPanel',
						})
					}
					isDisabled={!contentId || !spaceKey || loading}
					testId="add-label-button"
				/>
			</UFOLoadHold>
		) : null;
	};

	return (
		<div data-testId="labels-section" css={highlight && styles.highlight} ref={scrollToRef}>
			<DetailRow itemName={formatMessage(i18n.labelsSectionTitle)} icon={<TagIcon label="" />}>
				{renderLabelsSectionContent()}
			</DetailRow>
			{labels.length > 0 && (
				<Labels
					labels={labels}
					refetch={refetch}
					hasEditPermissions={hasEditPermissions}
					handleDeleteLabelClick={handleDeleteLabelClick}
				/>
			)}
		</div>
	);
};

const fadeOutHighlightKeyframes = keyframes({
	from: {
		backgroundColor: token('color.background.accent.yellow.subtlest'),
		borderLeftColor: token('color.border.accent.yellow'),
	},
	to: {
		backgroundColor: 'transparent',
		borderLeftColor: 'transparent',
	},
});

const styles = cssMap({
	highlight: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
		backgroundColor: token('color.background.accent.yellow.subtlest'),
		marginInline: token('space.negative.300'),
		paddingInline: token('space.300'),
		borderLeftWidth: token('border.width.selected'),
		borderLeftColor: token('color.border.accent.yellow'),
		borderLeftStyle: 'solid',
		animation: `${fadeOutHighlightKeyframes} 1.5s ease-out 0.5s forwards`,
	},
});
